import { useSyncExternalStore } from "react";

import { api, baseUrl, unwrap } from "./client";
import type { components } from "./schema";

/**
 * The attendance stream: the server pushes every applied change in the organisation
 * (`GET /v1/attendance/stream`, server-sent events), so a change made in the agent's tray shows
 * in the console within a second instead of at the next poll. The stream is opened with a
 * short-lived ticket in its address (`POST /v1/attendance/stream-ticket`): a browser's
 * EventSource cannot send the bearer header. Events are not replayed, so every connection
 * starts by fetching the state again (`onConnect`), then applies what the stream sends.
 */
export type AttendanceChange = components["schemas"]["AttendanceChange"];

/** How long a hidden tab keeps its stream before letting it go; it reconnects when shown again. */
export const STREAM_HIDDEN_PAUSE_MS = 5 * 60_000;
/** How long to wait before reconnecting after each failure in a row, then every 30 seconds. */
export const STREAM_BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 30_000] as const;

// Whether a stream is connected right now, readable from any component (the "Live" line, the
// polling interval), kept outside React like the session.
let live = false;
const listeners = new Set<() => void>();
function setLive(next: boolean) {
  if (live === next) return;
  live = next;
  for (const listener of listeners) listener();
}
export const attendanceStream = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  isLive: (): boolean => live,
};

/** True while the console hears about attendance changes as they happen. */
export function useAttendanceLive(): boolean {
  return useSyncExternalStore(attendanceStream.subscribe, attendanceStream.isLive);
}

const fetchTicket = async (): Promise<string> => (await unwrap(api.POST("/v1/attendance/stream-ticket"))).ticket;

type Handlers = {
  /** A change arrived. */
  onChange: (change: AttendanceChange) => void;
  /** The stream is (re)connected: fetch the state again, since nothing was replayed. */
  onConnect: () => void;
  /** The organisation's policy or settings changed: read them again. */
  onPolicy?: () => void;
  /** A person's computer changed (the Zotula app quit or running again): read the live list and the devices again. */
  onDevice?: () => void;
};

type Deps = {
  ticket: () => Promise<string>;
  EventSource: typeof EventSource;
  document: Document;
};

/**
 * Keep a stream open until the returned function is called: a fresh ticket for every connection,
 * a longer wait after each failure in a row, and a pause once the tab has been hidden for five
 * minutes (reconnecting the moment it is shown again). The browser's own retry is never used: it
 * would reuse the ticket, which is good for one stream.
 */
export function connectAttendanceStream(on: Handlers, deps: Partial<Deps> = {}): () => void {
  const ticket = deps.ticket ?? fetchTicket;
  const Source = deps.EventSource ?? globalThis.EventSource;
  const doc = deps.document ?? document;
  let source: EventSource | null = null;
  let stopped = false;
  let paused = false;
  let connecting = false;
  let failures = 0;
  let reconnect: ReturnType<typeof setTimeout> | undefined;
  let hiddenFor: ReturnType<typeof setTimeout> | undefined;

  const closeSource = () => {
    source?.close();
    source = null;
    setLive(false);
  };

  const scheduleReconnect = () => {
    if (stopped || paused) return;
    // Spread by up to half again, so every open console does not reconnect, and refetch, in the same second after a restart.
    const wait = STREAM_BACKOFF_MS[Math.min(failures, STREAM_BACKOFF_MS.length - 1)]! * (1 + Math.random() / 2);
    failures += 1;
    clearTimeout(reconnect);
    reconnect = setTimeout(() => void connect(), wait);
  };

  const connect = async () => {
    if (stopped || paused || connecting || source) return;
    connecting = true;
    let value: string;
    try {
      value = await ticket();
    } catch {
      connecting = false;
      scheduleReconnect(); // signed out, offline, or at the cap: try again later
      return;
    }
    connecting = false;
    if (stopped || paused) return;
    const stream = new Source(`${baseUrl}/v1/attendance/stream?ticket=${encodeURIComponent(value)}`);
    source = stream;
    stream.onopen = () => {
      failures = 0;
      setLive(true);
      on.onConnect();
    };
    stream.addEventListener("attendance", (event) => {
      try {
        on.onChange(JSON.parse((event as MessageEvent<string>).data) as AttendanceChange);
      } catch {
        // Not JSON: nothing to apply. The next fetch shows the state anyway.
      }
    });
    stream.addEventListener("policy", () => on.onPolicy?.());
    stream.addEventListener("device", () => on.onDevice?.());
    stream.onerror = () => {
      if (source !== stream) return; // already replaced
      closeSource();
      scheduleReconnect();
    };
  };

  const onVisibility = () => {
    clearTimeout(hiddenFor);
    if (doc.visibilityState === "hidden") {
      hiddenFor = setTimeout(() => {
        paused = true;
        clearTimeout(reconnect);
        closeSource();
      }, STREAM_HIDDEN_PAUSE_MS);
    } else if (paused) {
      paused = false;
      failures = 0;
      void connect();
    }
  };
  doc.addEventListener("visibilitychange", onVisibility);
  onVisibility();
  void connect();

  return () => {
    stopped = true;
    clearTimeout(reconnect);
    clearTimeout(hiddenFor);
    doc.removeEventListener("visibilitychange", onVisibility);
    closeSource();
  };
}
